import styled, {css} from "styled-components";
import {useEffect, useState} from "react";
import Text from "../../../core/utils/Text";
import {useRunAfterUpdate} from "../../../core/hooks/common/useRunAfterUpdate"
import {onInputValueChangeUtil} from "../../../core/utils/onInputValueChange"
import { DText, Flex } from "../../../styles/CommonStyles";
import {p2e, stringToNumber} from "../../../core/utils/common";
import {BsEyeFill, BsEyeSlashFill} from "react-icons/bs";


const Input = (props) => {

    const {
        value,
        onInputChange,
        label,
        width,
        height,
        valid,
        error,
        disabled,
        number,
        direction,
        normal,
        padding,
        currency = null,
        size = 'big',
        code = false,
        password = false
    } = props

    const runAfterUpdate = useRunAfterUpdate()

    const STATUS = {
        NORMAL: 'normal',
        ERROR: 'error',
        VALID: 'valid'
    }

    const [status, setStatus] = useState()

    const [animate, setAnimate] = useState(false)

    /**
     * hiding case - ex password
     */
    const [hidden, setHidden] = useState(password)
    const setShowMode = () => setHidden(false)
    const setHideMode = () => setHidden(true)

    const onValueChange = (e) => {

        let value = e?.target?.value
        if (number) {
          value = onInputValueChangeUtil(e, runAfterUpdate)
        }
        if (code) {
            value = p2e(value)
            value = value.replace( /[^\d.,]/g , '' )
        }
        onInputChange(value)
    }

    useEffect(() => {

        if ((value || error) && valid === false) setStatus(STATUS.ERROR)
        else if (value && valid) setStatus(STATUS.VALID)
        else setStatus(STATUS.NORMAL)

    }, [value, valid])


    return (
        <InputWrapper
            {...props}
        >
            <InputBody
                size={size}
                width={width}
                height={height}
                onFocus={() => setAnimate(true)}
                onBlur={() => setAnimate(false)}
                animate={animate}
                status={status}
                value={value}
                normal={normal}
            >

                <Label
                    size={size}
                    animate={animate}
                    status={status}
                    normal={normal}
                    value={value}
                >
                    <Text tid={label} />
                </Label>
                <Flex fw style={{ direction }}>
                  <TInput
                      {...props}
                      key={label}
                      size={size}
                      value={value}
                      onChange={onValueChange}
                      disabled={disabled}
                      type={password && ( hidden ? 'password' : 'text' )}
                  />
                  {currency &&
                    <Currency>
                      {currency.toUpperCase().substring(0, 4)}
                    </Currency>
                  }
                    {password && (
                        hidden ?
                            <ShowIcon size={'18'} onClick={setShowMode} />
                            :
                            <HideIcon size={'18'} onClick={setHideMode} />
                    )}

                </Flex>

                {(error && !valid) &&
                    <Error>
                        <Text tid={error} />
                    </Error>
                }

            </InputBody>
        </InputWrapper>

    )
}


const InputWrapper = styled.div`
  width: ${props => props.width || '100%'};
  height: ${props => props.height || '100%'};
  background-color: inherit;
  margin: 5px 0 20px 0;
  min-width: ${props => props.minWidth && props.minWidth};
  ${props => props.number && css`
    direction: ltr;
  `};
`


const InputBody = styled.div`
  width: 100%;
  height: 100%;
  min-height: ${props => props.size === 'big' ? '50px' : '42px'};
  min-width: 180px;
  position: relative;
  border: 1px solid ${props => props.theme.color}20;
  border-radius: 4px;
  z-index: 2;
  background-color: inherit;
  
  ${props => props.animate && css`
    border: 1px solid ${props => props.theme.active};
  `};

  ${props => props.status === 'valid' && css`
    border: 1px solid ${props => props.theme.mainGreen};
  `};

  ${props => props.status === 'error' && css`
    border: 1px solid ${props => props.theme.mainRed}90;
  `};

  ${props => (props.status === 'normal' && props.animate) && css`
    border: 1px solid ${props => props.theme.active};
  `};
  
  ${props => props.normal && css`
    border: 1px solid ${props => props.theme.color}20;
  `};

`


const TInput = styled.input`
  width: 100%;
  height: 100%;
  min-height: ${props => props.size === 'big' ? '50px' : '42px'};
  padding: 6px 12px;
  outline: none;
  border: none;
  background-color: transparent;
  color: ${props => props.theme.color};
  letter-spacing: 1.1px;
  font-size: 0.9rem;
  position: relative;
  z-index: 2;
  direction: ${props => props.dir && props.dir};
  font-family: 'Vazir';

  ${props => props.size === 'small' && css`
    font-size: 0.8rem;
  `};

  -webkit-text-fill-color: ${props => props.theme.color};
`


const Label = styled.div`
  position: absolute;
  font-size: 0.8rem;
  top: calc(50% - 0.6rem);
  background-color: inherit;
  padding: 0 5px;
  letter-spacing: 1.2px;
  
  ${props => props.theme.english && css`
      left: 10px;
  `};

  ${props => !props.theme.english && css`
      right: 10px;
  `};

  color: ${props => props.theme.color};

  ${props => props.animate && css`
    color: ${props => props.theme.active};
  `};
  
  ${props => (props.value || props.value === 0 || props.animate) && css`
    top: -22%;
    font-size: 0.8rem;
    z-index: 10;
  `};

  ${props => props.status === 'valid' && css`
    color: ${props => props.theme.mainGreen};
  `};

  ${props => props.status === 'error' && css`
    color: ${props => props.theme.mainRed}90;
  `};

  ${props => props.normal && css`
    color: ${props => props.theme.color};
  `};
  
  transition: all 0.3s;
  z-index: 1;

  ${props => props.size === 'small' && css`
    font-size: 0.7rem;
  `};
`

const Error = styled.div`
  position: absolute;
  bottom: -20px;
  color: ${props => props.theme.mainRed}90;
  font-size: 0.7rem;
  ${props => props.theme.english && css`
      left: 10px;
  `};

  ${props => !props.theme.english && css`
      right: 10px;
  `};
`


const Currency = styled.div`
  width: 48px;
  height: 100%;
  border-left: 1px solid ${props => props.theme.color}20;
  font-size: 0.8rem;
  color: ${props => props.theme.color};
  display: flex;
  justify-content: center;
  align-items: center;
`

const ShowIcon = styled(BsEyeFill)`
  color: ${props => props.theme.color}90;
  cursor: pointer;
  margin: 0 10px;
  transition: all 0.2s;
  &:hover{
    color: ${props => props.theme.color};
  };
`

const HideIcon = styled(BsEyeSlashFill)`
  color: ${props => props.theme.color}90;
  cursor: pointer;
  margin: 0 10px;
  transition: all 0.2s;
  &:hover{
    color: ${props => props.theme.color};
  };
`


export default Input;
